import { Action, ActionCreator, Reducer } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { HttpClient } from '@common/clients/HttpClient.js';
import type { RootState } from '@src/store/index.js';
import { checkScenario } from '@src/util/scenario.js';

// action types

export const VALIDATE = 'auth/VALIDATE';
export const PROCESSING = 'auth/PROCESSING';
export const ERROR = 'auth/ERROR';

// action creators

export const validate: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
  getState,
): Promise<Action> => {
  try {
    dispatch({ type: PROCESSING });
    const { config } = getState();
    const {
      apiUri,
      accessToken: configAccessToken,
      customerToken,
      registerToken,
      traceid,
    } = config;

    const { isRaittNoTokensWithInterception, isAuthOverride } = checkScenario(config);

    const headers: Record<string, string> = {};
    if (isAuthOverride) {
      headers['x-authorization-override'] = customerToken
        ? `Customer ${customerToken}`
        : `Register ${registerToken}`;
    }

    let path;
    if (isRaittNoTokensWithInterception) {
      path = '/identity';
    } else {
      path = registerToken
        ? '/token'
        : '/userprofile';
      headers.Authorization = configAccessToken
        ? `Bearer ${configAccessToken}`
        : `Register ${registerToken}`;
    }

    const httpClient = new HttpClient({
      apiUrl: apiUri,
      headers,
      traceid,
    });

    const body = await httpClient.get(path);

    const {
      registerId,
      lcId,
      spId,
      accessToken, // coms internal access token
      username = 'unknown',
    } = body as {
      registerId?: string;
      lcId?: string;
      spId?: string;
      accessToken?: string;
      username: string;
    };

    return dispatch({
      type: VALIDATE,
      registerId,
      lcId,
      spId,
      accessToken,
      username,
    });
  } catch (error) {
    dispatch({ type: ERROR, error });
    throw error;
  }
};

export type AuthState = {
  accessToken?: string;
  spId?: string;
  username?: string;
  lcId?: string;
  registerId?: string;
  processing: boolean;
  error: boolean;
};

const initial: AuthState = {
  processing: false,
  error: false,
};

type AuthAction = Action<typeof VALIDATE | typeof PROCESSING | typeof ERROR> & AuthState;

// reducer

export const reducer: Reducer<AuthState, AuthAction> = (
  state = initial,
  action = {
    // @ts-expect-error TODO: fix this
    type: undefined,
    ...initial,
  },
) => {
  const {
    type,
    registerId,
    lcId,
    spId,
    accessToken,
    username,
    error,
  } = action;
  switch (type) {
    case VALIDATE:
      return {
        registerId,
        lcId,
        spId,
        accessToken,
        username,
        processing: false,
        error: false,
      };
    case PROCESSING:
      return { ...state, processing: true, error: false };
    case ERROR:
      return { ...state, processing: false, error };
    default:
      return state;
  }
};
